import { useEffect, useState } from "react";
import { Boxes, AlertTriangle, PackageSearch, Truck, DollarSign, Search, X } from "lucide-react";
import type { Part } from "@sbd-tcic/shared-types";
import { api, ApiClientError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../lib/toast";
import { KpiTile } from "../components/KpiTile";
import { DataTable, type Column } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";

interface InventoryKpis {
  totalParts: number;
  outOfStock: number;
  lowStock: number;
  onBackOrder: number;
  inventoryValue: number;
}

interface DiscrepancyRecord {
  id: string;
  partNumber: string;
  reason: string;
  openedDate: string;
  resolvedDate: string | null;
  status: "Open" | "Resolved";
}

type PartDetail = Part & { openDiscrepancies: DiscrepancyRecord[] };

const STATUS_TABS = ["All", "In Stock", "Low Stock", "Out of Stock"] as const;

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function Inventory() {
  const { can } = useAuth();
  const { toast } = useToast();

  const [kpis, setKpis] = useState<InventoryKpis | null>(null);
  const [query, setQuery] = useState("");
  const [statusTab, setStatusTab] = useState<(typeof STATUS_TABS)[number]>("All");
  const [results, setResults] = useState<Part[]>([]);
  const [loadingResults, setLoadingResults] = useState(true);

  const [selectedPartNumber, setSelectedPartNumber] = useState<string | null>(null);
  const [detail, setDetail] = useState<PartDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [discrepancyReason, setDiscrepancyReason] = useState("");
  const [scrapReason, setScrapReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.get<InventoryKpis>("/inventory/kpis").then(setKpis);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      setLoadingResults(true);
      const params = new URLSearchParams();
      if (query.trim()) params.set("query", query.trim());
      if (statusTab !== "All") params.set("status", statusTab);
      void api
        .get<Part[]>(`/parts?${params.toString()}`)
        .then((rows) => {
          if (cancelled) return;
          setResults(rows);
        })
        .finally(() => {
          if (!cancelled) setLoadingResults(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, statusTab]);

  async function openPart(partNumber: string) {
    setSelectedPartNumber(partNumber);
    setDetail(null);
    setDetailLoading(true);
    setDiscrepancyReason("");
    setScrapReason("");
    try {
      const data = await api.get<PartDetail>(`/parts/${encodeURIComponent(partNumber)}`);
      setDetail(data);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to load part detail.", "error");
      setSelectedPartNumber(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function refreshDetail() {
    if (!selectedPartNumber) return;
    const data = await api.get<PartDetail>(`/parts/${encodeURIComponent(selectedPartNumber)}`);
    setDetail(data);
  }

  async function submitDiscrepancy() {
    if (!selectedPartNumber || !discrepancyReason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/parts/${encodeURIComponent(selectedPartNumber)}/discrepancy`, { reason: discrepancyReason.trim() });
      toast("Discrepancy reported.", "success");
      setDiscrepancyReason("");
      await refreshDetail();
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to report discrepancy.", "error");
    } finally {
      setBusy(false);
    }
  }

  async function submitScrapRequest() {
    if (!selectedPartNumber || !scrapReason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/parts/${encodeURIComponent(selectedPartNumber)}/scrap-request`, { reason: scrapReason.trim() });
      toast("Scrap request submitted for approval.", "success");
      setScrapReason("");
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to submit scrap request.", "error");
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<Part>[] = [
    { key: "partNumber", header: "Part #", render: (p) => <span className="font-bold text-brand-blue">{p.partNumber}</span> },
    { key: "description", header: "Description", render: (p) => p.description },
    { key: "brand", header: "Brand", render: (p) => p.brand },
    { key: "bin", header: "Bin", render: (p) => p.bin || "—" },
    { key: "onHand", header: "On Hand", render: (p) => p.onHand },
    { key: "available", header: "Available", render: (p) => p.available },
    { key: "status", header: "Status", render: (p) => <StatusBadge status={p.status} /> },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3.5">
        <KpiTile icon={Boxes} iconColor="blue" label="Total Parts" value={kpis?.totalParts ?? 0} />
        <KpiTile icon={AlertTriangle} iconColor="red" label="Out of Stock" value={kpis?.outOfStock ?? 0} />
        <KpiTile icon={PackageSearch} iconColor="orange" label="Low Stock" value={kpis?.lowStock ?? 0} />
        <KpiTile icon={Truck} iconColor="purple" label="On Backorder" value={kpis?.onBackOrder ?? 0} />
        <KpiTile icon={DollarSign} iconColor="green" label="Inventory Value" value={money(kpis?.inventoryValue ?? 0)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5 items-start">
        <div className="card p-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3.5">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input
                className="input pl-8"
                placeholder="Search by part number or description…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-1.5">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab}
                  className={`px-3 py-1.5 rounded-md text-xs font-bold whitespace-nowrap transition-colors ${
                    statusTab === tab ? "bg-brand-blue text-white" : "bg-surface text-ink-muted hover:text-ink"
                  }`}
                  onClick={() => setStatusTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
          <DataTable
            columns={columns}
            rows={results}
            rowKey={(p) => p.partNumber}
            onRowClick={(p) => void openPart(p.partNumber)}
            emptyMessage={loadingResults ? "Loading parts…" : "No parts match this search."}
          />
        </div>

        <div className="card p-5 h-fit sticky top-20">
          {!selectedPartNumber ? (
            <div className="text-sm text-ink-muted text-center py-10">Select a part to view its full record.</div>
          ) : detailLoading || !detail ? (
            <div className="text-sm text-ink-muted text-center py-10">Loading part detail…</div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-3.5">
                <div>
                  <div className="text-lg font-extrabold">{detail.partNumber}</div>
                  <div className="text-sm text-ink-muted">{detail.description}</div>
                </div>
                <button
                  aria-label="Close"
                  className="text-ink-muted hover:text-ink"
                  onClick={() => {
                    setSelectedPartNumber(null);
                    setDetail(null);
                  }}
                >
                  <X size={16} />
                </button>
              </div>
              <div className="mb-3.5">
                <StatusBadge status={detail.status} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <div>
                  <div className="text-ink-muted text-xs">Brand</div>
                  <div className="font-semibold">{detail.brand}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">Category</div>
                  <div className="font-semibold">{detail.category}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">Manufacturer</div>
                  <div className="font-semibold">{detail.manufacturer}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">Bin</div>
                  <div className="font-semibold">{detail.bin || "Unassigned"}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">On Hand</div>
                  <div className="font-semibold">{detail.onHand}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">Available</div>
                  <div className="font-semibold">{detail.available}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">Allocated</div>
                  <div className="font-semibold">{detail.allocated}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">On Order</div>
                  <div className="font-semibold">{detail.onOrder}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">Min Qty</div>
                  <div className="font-semibold">{detail.minQty}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">Safety Stock</div>
                  <div className="font-semibold">{detail.safetyStock}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">UOM</div>
                  <div className="font-semibold">{detail.uom}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">Std Cost</div>
                  <div className="font-semibold">{money(detail.stdCost)}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">List Price</div>
                  <div className="font-semibold">{money(detail.listPrice)}</div>
                </div>
                <div>
                  <div className="text-ink-muted text-xs">Your Price</div>
                  <div className="font-semibold">{money(detail.yourPrice)}</div>
                </div>
              </div>

              {detail.openDiscrepancies.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-ink-muted mb-1.5">Open Discrepancies</div>
                  <div className="flex flex-col gap-1.5">
                    {detail.openDiscrepancies.map((d) => (
                      <div key={d.id} className="text-xs border border-border rounded-md px-2.5 py-1.5">
                        <div className="font-semibold">{d.reason}</div>
                        <div className="text-ink-muted">Opened {d.openedDate.slice(0, 10)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {can("inventory.write") && (
                <div className="flex flex-col gap-3.5 border-t border-border pt-3.5">
                  <div>
                    <label className="field-label">Report Discrepancy</label>
                    <div className="flex gap-2">
                      <input
                        className="input"
                        placeholder="Reason for discrepancy…"
                        value={discrepancyReason}
                        onChange={(e) => setDiscrepancyReason(e.target.value)}
                      />
                      <button className="btn-outline" disabled={busy || !discrepancyReason.trim()} onClick={() => void submitDiscrepancy()}>
                        Report
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="field-label">Request Scrap</label>
                    <div className="flex gap-2">
                      <input
                        className="input"
                        placeholder="Reason for scrap request…"
                        value={scrapReason}
                        onChange={(e) => setScrapReason(e.target.value)}
                      />
                      <button className="btn-danger" disabled={busy || !scrapReason.trim()} onClick={() => void submitScrapRequest()}>
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
